import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { money, timeOnly, todayIso } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { printSlip } from '../opd/FrontDeskDialogs';

interface DueVisit {
  appointmentId: string;
  encounterId: string | null;
  tokenNo: string | null;
  status: string;
  arrivedAt: string | null;
  patient: { id: string; mrn: string; fullName: string };
  doctor: { id: string; name: string };
  payerName: string | null;
  dueMinor: number;
  ordersDueMinor: number;
  invoices: { id: string; number: string; status: string; context: string; fromOrders: boolean; netMinor: number; patientDueMinor: number }[];
  waiver: { reason: string; status: string; at: string } | null;
}

const ORDER_CONTEXT_LABEL: Record<string, string> = {
  lab: 'dues_orders_lab',
  radiology: 'dues_orders_radiology',
};

function collectLabel(invoice: DueVisit['invoices'][number]): string {
  const kind = invoice.fromOrders ? ORDER_CONTEXT_LABEL[invoice.context] ?? 'dues_orders_other' : null;
  return kind ? tr('billing', 'collect_x_for_y', { number: invoice.number, kind: tr('billing', kind) }) : `${tr('billing', 'collect')} ${invoice.number}`;
}

export function OpdDuesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = useWorkingBranchId();
  const [date, setDate] = useState(todayIso());
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [declining, setDeclining] = useState<string | null>(null);

  useRealtimeInvalidate(branchId ? [`queue-branch:${branchId}`] : [], ['queue.updated'], [['opd-dues']]);
  const dues = useQuery({
    queryKey: ['opd-dues', branchId, date, cursor],
    queryFn: () => get<{ data: DueVisit[]; nextCursor: string | null }>(`/billing/opd-dues${qs({ branchId, date, cursor })}`),
    enabled: Boolean(branchId),
    refetchInterval: visibleInterval(60_000),
  });

  const closeWaiver = useMutation({
    mutationFn: ({ id, outcome, note }: { id: string; outcome: 'applied' | 'declined'; note?: string }) =>
      post(`/appointments/${id}/fee-waiver-request/close`, { outcome, note }),
    onSuccess: () => {
      setDeclining(null);
      void queryClient.invalidateQueries({ queryKey: ['opd-dues'] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_close_the_waiver_request'), describeError(error)),
  });

  const changeDate = (value: string) => {
    setDate(value);
    setCursor(undefined);
  };

  return (
    <>
      <PageHeader title={tr('billing', 'opd_dues')} description={tr('billing', 'visits_with_something_for_the_patient_to_pay')} />
      {!branchId && <Alert variant="warning">{tr('billing', 'your_account_has_no_branch_ask_an_administrator')}</Alert>}
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'date')}>
            <DatePicker value={date} onChange={changeDate} className="w-40" />
          </FilterField>
        }
        onReset={date !== todayIso() ? () => changeDate(todayIso()) : undefined}
      />
      <DataTable
        rows={dues.data?.data}
        loading={dues.isLoading} refreshing={dues.isFetching}
        error={dues.error}
        rowKey={(row) => row.appointmentId}
        empty={tr('billing', 'nothing_is_due_for_this_day')}
        footer={
          dues.data?.nextCursor ? (
            <Button variant="outline" size="sm" className="ms-auto" onClick={() => setCursor(dues.data?.nextCursor ?? undefined)}>
              {tr('billing', 'next_page')}
            </Button>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'token'), width: '70px', sort: (row) => row.tokenNo, cell: (row) => <span className="tabular">{row.tokenNo ?? '—'}</span> },
          {
            header: tr('billing', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn} · {row.doctor.name}
                  {row.arrivedAt ? ` · ${timeOnly(row.arrivedAt)}` : ''}
                </p>
              </div>
            ),
          },
          { header: tr('billing', 'payer'), sort: (row) => row.payerName, cell: (row) => row.payerName ?? tr('billing', 'nx_self_pay') },
          {
            header: tr('billing', 'due'),
            align: 'right',
            sort: (row) => row.dueMinor,
            cell: (row) => (
              <div className="text-end">
                <span className="tabular font-semibold">{money(row.dueMinor)}</span>
                {row.ordersDueMinor > 0 && (
                  <p className="text-xs text-muted-foreground">{tr('billing', 'dues_includes_orders', { amount: money(row.ordersDueMinor) })}</p>
                )}
              </div>
            ),
          },
          {
            header: tr('billing', 'waiver'),
            cell: (row) =>
              row.waiver?.status === 'pending' ? (
                <div className="space-y-1">
                  <Badge variant="warning">{tr('billing', 'waiver_asked')}</Badge>
                  <p className="max-w-xs text-xs text-muted-foreground">{row.waiver.reason}</p>
                </div>
              ) : null,
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex flex-wrap justify-end gap-1">
                {row.invoices
                  .filter((invoice) => invoice.patientDueMinor > 0)
                  .map((invoice) => (
                    <Button key={invoice.id} size="sm" onClick={() => navigate(`/billing/invoices/${invoice.id}`)}>
                      {collectLabel(invoice)}
                    </Button>
                  ))}
                {row.encounterId && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/billing/statement?encounterId=${encodeURIComponent(row.encounterId ?? '')}`)}>
                    {tr('billing', 'dues_visit_statement')}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => void printSlip(row.appointmentId)}>
                  {tr('billing', 'slip')}
                </Button>
                {row.waiver?.status === 'pending' && can('discount.apply', 'discount.approve') && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => closeWaiver.mutate({ id: row.appointmentId, outcome: 'applied' })}>
                      {tr('billing', 'discount_applied')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeclining(row.appointmentId)}>
                      {tr('billing', 'decline')}
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]}
      />
      {can('report.financial', 'invoice.read') && <UnbilledVisits date={date} branchId={branchId} />}
      {declining && (
        <ReasonDialog
          title={tr('billing', 'decline_waiver_title')}
          description={tr('billing', 'decline_waiver_description')}
          minLength={3}
          maxLength={400}
          submitLabel={tr('billing', 'decline')}
          destructive
          loading={closeWaiver.isPending}
          onSubmit={(note) => closeWaiver.mutate({ id: declining, outcome: 'declined', note })}
          onClose={() => setDeclining(null)}
        />
      )}
    </>
  );
}

const BILLING_ISSUE_LABEL: Record<string, string> = {
  no_fee_rule: 'No fee set for this doctor',
  no_price: 'No price for the fee',
  coverage_invalid: 'Coverage not valid on the day',
  error: 'Billing failed',
  not_billed: 'Not billed',
};

interface UnbilledVisit {
  id: string;
  slotAt: string;
  type: string;
  billingIssue: string;
  patient: { id: string; mrn: string; fullName: string };
  doctor: { id: string; name: string };
  payerName: string | null;
}

function UnbilledVisits({ date, branchId }: { date: string; branchId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const unbilled = useQuery({
    queryKey: ['opd-unbilled', branchId, date, cursor],
    queryFn: () =>
      get<{ data: UnbilledVisit[]; nextCursor: string | null }>(`/reports/opd-unbilled${qs({ from: date, to: date, branchId: branchId || undefined, cursor })}`),
  });
  const billNow = useMutation({
    mutationFn: (id: string) => post<{ billed: boolean }>(`/appointments/${id}/bill`, {}),
    onSuccess: (result) => {
      if (result.billed) toast.success(tr('billing', 'visit_billed'));
      else toast.error(tr('billing', 'could_not_bill_the_visit'), tr('billing', 'billing_still_blocked'));
      void queryClient.invalidateQueries({ queryKey: ['opd-unbilled'] });
      void queryClient.invalidateQueries({ queryKey: ['opd-dues'] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_bill_the_visit'), describeError(error)),
  });
  return (
    <div className="mt-6 space-y-2">
      <p className="font-medium">{tr('billing', 'seen_but_not_billed')}</p>
      <DataTable
        rows={unbilled.data?.data}
        loading={unbilled.isLoading} refreshing={unbilled.isFetching}
        error={unbilled.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'every_completed_visit_of_this_day_has_its')}
        footer={
          unbilled.data?.nextCursor ? (
            <Button variant="outline" size="sm" className="ms-auto" onClick={() => setCursor(unbilled.data?.nextCursor ?? undefined)}>
              {tr('billing', 'next_page')}
            </Button>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'time'), width: '80px', sort: (row) => row.slotAt, cell: (row) => timeOnly(row.slotAt) },
          {
            header: tr('billing', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn} · {row.doctor.name}
                </p>
              </div>
            ),
          },
          { header: tr('billing', 'payer'), sort: (row) => row.payerName, cell: (row) => row.payerName ?? tr('billing', 'nx_self_pay') },
          { header: tr('billing', 'why'), sort: (row) => row.billingIssue, cell: (row) => <Badge variant="warning">{BILLING_ISSUE_LABEL[row.billingIssue] ?? row.billingIssue}</Badge> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              can('invoice.create') ? (
                <Button size="sm" variant="outline" onClick={() => billNow.mutate(row.id)} loading={billNow.isPending && billNow.variables === row.id}>
                  {tr('billing', 'bill_now')}
                </Button>
              ) : null,
          },
        ]}
      />
    </div>
  );
}
